import React, { useState, useEffect, useRef } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import 'styles/Font.css';

// Utils import
import { getTokenStatus, clearAllAuthStorage } from 'utils/tokenManager';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from "config/apiConfig";

// Image imports
import arrowRightIcon from "assets/images/icon/arrow-right-icon.svg";
import checkIcon from "assets/images/icon/checked-icon.svg";

// Component imports
import TopNavigator from "components/layout/TopNavigator/TopNavigator";
import Footer from "components/layout/Footer/Footer";

/**
 * 날짜 포맷팅 유틸리티 (YYYY-MM-DD)
 */
const formatDateKey = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

function AttendanceHistory() {
    const navigate = useNavigate();
    const headerRef = useRef(null);
    const [headerHeight, setHeaderHeight] = useState(0);

    // --- Calendar State ---
    const [selectedDate, setSelectedDate] = useState(new Date());

    // 현재 보고 있는 달(Active Month)을 추적하는 상태
    const [activeDate, setActiveDate] = useState(new Date());

    const [attendanceLog, setAttendanceLog] = useState({});
    const [isLoading, setIsLoading] = useState(true);

    // 리스트 더보기 상태 관리 (기본 3개)
    const [visibleCount, setVisibleCount] = useState(3);

    // 보고 있는 달(activeDate)이 바뀌면 더보기 카운트 초기화
    useEffect(() => {
        setVisibleCount(3);
    }, [activeDate]);

    // 현재 '보고 있는 달'의 모든 출석 기록 필터링
    const getMonthlyRecords = () => {
        const currentYear = activeDate.getFullYear();
        const currentMonth = activeDate.getMonth() + 1;

        const records = Object.entries(attendanceLog)
            .filter(([dateKey]) => {
                const [y, m] = dateKey.split('-').map(Number);
                return y === currentYear && m === currentMonth;
            })
            .map(([dateKey, info]) => ({
                date: dateKey,
                ...info
            }));

        // 날짜순 정렬 (1일 -> 31일)
        return records.sort((a, b) => new Date(a.date) - new Date(b.date));
    };

    const monthlyRecords = getMonthlyRecords();

    // 더보기 버튼 핸들러
    const handleLoadMore = () => {
        setVisibleCount((prev) => prev + 5);
    };

    // --- 동적 헤더 높이 측정 ---
    useEffect(() => {
        const measureHeight = () => {
            if (headerRef.current) {
                setHeaderHeight(headerRef.current.offsetHeight);
            }
        };
        measureHeight();
        window.addEventListener('resize', measureHeight);
        return () => window.removeEventListener('resize', measureHeight);
    }, []);

    // --- API 호출 및 데이터 가공 로직 ---
    useEffect(() => {
        const fetchAttendanceHistory = async () => {
            const { token } = getTokenStatus();

            if (!token) {
                // alert("로그인이 필요합니다."); // 필요 시 활성화
                navigate('/');
                return;
            }

            try {
                const response = await fetch(`${API_BASE_URL}/inout`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    const resData = data.data;

                    if (resData && resData.list) {
                        const newLog = {};

                        resData.list.forEach(item => {
                            const dateObj = new Date(item.date);
                            if (!isNaN(dateObj)) {
                                const key = formatDateKey(dateObj);
                                const hours = String(dateObj.getHours()).padStart(2, '0');
                                const minutes = String(dateObj.getMinutes()).padStart(2, '0');
                                const timeStr = `${hours}:${minutes}`;

                                newLog[key] = {
                                    time: timeStr,
                                    branch: item.branch || '전 지점'
                                };
                            }
                        });
                        setAttendanceLog(newLog);
                    }
                } else if (response.status === 401) {
                    clearAllAuthStorage();
                    navigate('/');
                } else {
                    console.error("출석 내역 불러오기 실패:", response.status);
                }
            } catch (error) {
                console.error("API Error:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchAttendanceHistory();
    }, [navigate]);


    // --- 캘린더 타일 커스텀 ---
    const tileContent = ({ date, view }) => {
        if (view === 'month') {
            const dateKey = formatDateKey(date);
            if (attendanceLog[dateKey]) {
                return (
                    <div className="dotWrapper">
                        <div className="attendanceDot"></div>
                    </div>
                );
            }
        }
        return null;
    };

    // 이번 달 출석 횟수 계산 (activeDate 기준)
    const getMonthlyCount = (date) => {
        const currentYear = date.getFullYear();
        const currentMonth = date.getMonth();

        return Object.keys(attendanceLog).filter(key => {
            const [y, m] = key.split('-').map(Number);
            return y === currentYear && m === (currentMonth + 1);
        }).length;
    };

    // 날짜 선택 시 핸들러
    const handleDateChange = (newDate) => {
        setSelectedDate(newDate);
        setActiveDate(newDate); // 날짜 클릭 시 해당 달로 activeDate도 동기화
    };

    // 캘린더 뷰 변경(월 이동) 시 핸들러
    const handleActiveStartDateChange = ({ activeStartDate }) => {
        setActiveDate(activeStartDate);
    };

    const dynamicMainStyle = {
        ...staticStyles.mainContent,
        paddingTop: `${headerHeight}px`,
    };

    return (
        <div className="backgroundWrapper" style={staticStyles.backgroundWrapper}>
            <style>{calendarCustomCss}</style>

            <div style={staticStyles.fixedHeaderWrapper} ref={headerRef}>
                <div className="statusBar" style={staticStyles.statusBar} />
                <TopNavigator title="출석 내역" />
            </div>

            <main style={dynamicMainStyle}>
                {isLoading ? (
                    <div style={{ ...staticStyles.summaryCard, textAlign: 'center', color: 'white' }}>
                        데이터를 불러오는 중입니다...
                    </div>
                ) : (
                    <>
                        {/* [수정] activeDate 기준으로 월과 횟수 표시 */}
                        <div style={staticStyles.summaryCard}>
                            <div style={staticStyles.summaryTextBase}>
                                <span style={staticStyles.summaryHighlight}>
                                    {activeDate.getMonth() + 1}월
                                </span>
                                은 총
                                <span style={staticStyles.summaryHighlight}>
                                    {' '}{getMonthlyCount(activeDate)}회
                                </span>
                                {' '}운동하셨네요! 🔥
                            </div>
                        </div>

                        <div style={staticStyles.calendarContainer}>
                            <Calendar
                                onChange={handleDateChange} // 핸들러 변경
                                value={selectedDate}
                                locale="ko-KR"
                                calendarType="gregory"
                                formatDay={(locale, date) => date.getDate()}
                                tileContent={tileContent}
                                next2Label={null}
                                prev2Label={null}
                                showNeighboringMonth={false}
                                // 월 이동 시 activeDate 업데이트
                                onActiveStartDateChange={handleActiveStartDateChange}
                            />
                        </div>

                        <div style={staticStyles.detailSection}>
                            {/* [수정] activeDate 기준으로 타이틀 표시 */}
                            <p style={staticStyles.detailTitle}>
                                {activeDate.getMonth() + 1}월 운동 기록
                            </p>

                            {monthlyRecords.length > 0 ? (
                                <>
                                    <div style={staticStyles.listContainer}>
                                        {monthlyRecords.slice(0, visibleCount).map((record) => {
                                            const [y, m, d] = record.date.split('-');
                                            return (
                                                <div key={record.date} style={staticStyles.attendanceCard}>
                                                    <div style={staticStyles.cardLeft}>
                                                        <div style={staticStyles.iconBox}>
                                                            <img src={checkIcon} alt="출석" style={{ width: '16px' }} />
                                                        </div>
                                                        <div style={staticStyles.infoText}>
                                                            <div style={staticStyles.branchText}>
                                                                {parseInt(m)}월 {parseInt(d)}일
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div style={staticStyles.cardRight}>
                                                        <span style={staticStyles.statusBadge}>출석 완료</span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {visibleCount < monthlyRecords.length && (
                                        <button
                                            onClick={handleLoadMore}
                                            style={staticStyles.loadMoreButton}
                                        >
                                            더보기 <img src={arrowRightIcon} alt="more"
                                                style={staticStyles.loadMoreArrow} />
                                        </button>
                                    )}
                                </>
                            ) : (
                                <div style={staticStyles.emptyState}>
                                    이 달은 운동 기록이 없어요. 🏝️
                                </div>
                            )}
                        </div>
                    </>
                )}

                <Footer />
            </main>
        </div>
    );
}

// ... (calendarCustomCss 및 staticStyles는 기존과 동일하게 유지) ...
const calendarCustomCss = `
    /* 캘린더 전체 배경 및 폰트 */
    .react-calendar {
        width: 100%;
        background-color: transparent;
        border: none;
        font-family: 'Pretendard', sans-serif;
    }
    
    /* 상단 네비게이션 (년/월) */
    .react-calendar__navigation {
        height: 50px;
        margin-bottom: 10px;
    }
    .react-calendar__navigation button {
        color: #FFFFFF;
        font-size: 18px;
        font-weight: 700;
        background: none;
    }
    .react-calendar__navigation button:enabled:hover,
    .react-calendar__navigation button:enabled:focus {
        background-color: transparent;
    }
    
    /* 요일 표시 (월, 화, 수...) */
    .react-calendar__month-view__weekdays {
        text-align: center;
        text-transform: uppercase;
        font-weight: 400;
        font-size: 14px;
        color: #9A9A9A; /* 회색 폰트 */
        text-decoration: none; /* 밑줄 제거 */
        abbr { text-decoration: none; }
    }
    
    /* 날짜 타일 공통 */
    .react-calendar__tile {
        height: 52px; /* 타일 높이 확보 */
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-items: center;
        padding: 10px 0;
        background: none;
        color: #FFFFFF;
        font-size: 16px;
        font-weight: 500;
        position: relative;
    }

    /* 타일 hover/focus 제거 (모바일 느낌) */
    .react-calendar__tile:enabled:hover,
    .react-calendar__tile:enabled:focus {
        background-color: transparent;
        color: #FFFFFF;
    }

    /* 오늘 날짜 */
    .react-calendar__tile--now {
        background: transparent;
        color: #8052FF; /* 보라색 텍스트 */
        font-weight: 700;
    }
    .react-calendar__tile--now:enabled:hover,
    .react-calendar__tile--now:enabled:focus {
        background: transparent;
        color: #8052FF;
    }

    /* 선택된 날짜 (원형 배경) */
    .react-calendar__tile--active {
        background: transparent !important;
        color: #FFFFFF !important;
    }
    /* 선택된 날짜에 가상요소로 원 그리기 */
    .react-calendar__tile--active abbr {
        background: linear-gradient(90deg, #8052FF 0%, #0231FB 100%);
        width: 28px;
        height: 28px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-top: -4px; /* 위치 미세 조정 */
    }

    /* 출석 점 (Dot) 스타일 */
    .dotWrapper {
        display: flex;
        justify-content: center;
        margin-top: 4px;
    }
    .attendanceDot {
        width: 6px;
        height: 6px;
        background-color: #0231FB; /* 출석 색상 (파랑) */
        border-radius: 50%;
    }
    
    /* 선택된 날짜의 점 색상은 흰색으로 변경 */
    .react-calendar__tile--active .attendanceDot {
        background-color: #FFFFFF;
    }
`;

const staticStyles = {
    backgroundWrapper: {
        background: '#0D042D',
        width: '100%',
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        boxSizing: 'border-box',
        position: 'relative',
    },
    fixedHeaderWrapper: {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        zIndex: 100,
        backgroundColor: '#0D042D',
        display: 'flex',
        flexDirection: 'column',
    },
    statusBar: {
        flexShrink: 0,
        backgroundColor: 'transparent',
    },
    mainContent: {
        flex: 1,
        padding: '0 24px 40px', // 좌우 24px, 하단 40px
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        overflowY: 'auto',
    },

    // --- 요약 카드 ---
    summaryCard: {
        marginTop: '20px',
        background: 'linear-gradient(90deg, #1B1931 0%, #2A2845 100%)',
        borderRadius: '12px',
        padding: '20px',
        textAlign: 'center',
        border: '1px solid #3A394A',
    },
    summaryTextBase: {
        fontFamily: 'Pretendard',
        fontSize: '18px',
        color: '#FFFFFF',
        fontWeight: 400,
    },
    summaryHighlight: {
        fontWeight: 700,
        color: '#8052FF', // 포인트 컬러
        fontSize: '20px',
    },

    // --- 캘린더 컨테이너 ---
    calendarContainer: {
        background: '#1B1931',
        borderRadius: '16px',
        padding: '20px 10px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
    },

    // --- 상세 정보 섹션 ---
    detailSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
    },
    detailTitle: {
        fontFamily: 'Pretendard',
        fontSize: '18px',
        fontWeight: 600,
        color: '#FFFFFF',
        margin: '0 0 4px 4px',
    },
    attendanceCard: {
        background: '#1B1931',
        borderRadius: '12px',
        padding: '16px 20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderLeft: '4px solid #8052FF', // 포인트 라인
    },
    cardLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
    },
    iconBox: {
        width: '32px',
        height: '32px',
        borderRadius: '50%',
        background: 'rgba(128, 82, 255, 0.2)', // 반투명 보라
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    infoText: {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
    },
    branchText: {
        fontSize: '16px',
        fontWeight: 600,
        color: '#FFFFFF',
    },
    timeText: {
        fontSize: '14px',
        fontWeight: 400,
        color: '#9A9A9A',
    },
    cardRight: {
        display: 'flex',
        alignItems: 'center',
    },
    statusBadge: {
        fontSize: '12px',
        color: '#8052FF',
        fontWeight: 600,
        background: '#2A2845',
        padding: '4px 8px',
        borderRadius: '4px',
    },
    emptyState: {
        width: '100%',
        padding: '30px 0',
        textAlign: 'center',
        color: '#555',
        fontSize: '14px',
        background: '#1B1931',
        borderRadius: '12px',
    },

    // 리스트 컨테이너 (카드 사이 간격)
    listContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
    },

    // 더보기 버튼
    loadMoreButton: {
        width: '100%',
        padding: '12px 0',
        marginTop: '8px',
        background: 'transparent',
        border: '1px solid #3A394A',
        borderRadius: '8px',
        color: '#9A9A9A',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '6px',
        fontFamily: 'Pretendard',
    },

    // 더보기 화살표 (90도 회전하여 아래쪽 가리키게 함)
    loadMoreArrow: {
        width: '6px',
        height: '12px',
        transform: 'rotate(90deg)',
        opacity: 0.6,
    }
};

export default AttendanceHistory;